/**
 * Módulo de Instalación PWA (Progressive Web App) - Detectimáticas
 * Gestiona el registro del Service Worker, captura el evento beforeinstallprompt para Android/Desktop
 * y proporciona guías modales paso a paso para Android e iOS.
 */

let deferredPrompt = null;

function isIos() {
    const userAgent = window.navigator.userAgent.toLowerCase();
    return /iphone|ipad|ipod/.test(userAgent);
}

function isAndroid() {
    const userAgent = window.navigator.userAgent.toLowerCase();
    return /android/.test(userAgent);
}

function isInStandaloneMode() {
    return (window.matchMedia('(display-mode: standalone)').matches) || 
           (window.navigator.standalone === true) ||
           (document.referrer && document.referrer.includes('android-app://'));
}

/**
 * Registra activamente el Service Worker si la plataforma lo soporta.
 */
export function registerServiceWorker() {
    if ('serviceWorker' in navigator && window.location.protocol !== 'file:') {
        window.addEventListener('load', () => {
            navigator.serviceWorker.register('./sw.js', { scope: './' })
                .then((reg) => {
                    console.log('[PWA] Service Worker registrado exitosamente con scope:', reg.scope);
                })
                .catch((err) => {
                    console.warn('[PWA] No se pudo registrar el Service Worker:', err);
                });
        });
    }
}

/**
 * Inicializa la lógica de instalación PWA
 */
export function initPWAInstaller() {
    // 1. Intentar registrar el Service Worker en primer lugar
    registerServiceWorker();

    // 2. Si ya está instalada o ejecutando en standalone, no mostramos nada
    if (isInStandaloneMode()) {
        console.log('[PWA] La aplicación ya está instalada y ejecutándose en modo Standalone.');
        return;
    }

    // 3. Registrar el evento beforeinstallprompt de Chrome/Android/Edge
    window.addEventListener('beforeinstallprompt', (e) => {
        e.preventDefault();
        deferredPrompt = e;
        console.log('[PWA] Evento beforeinstallprompt capturado y listo.');
        showInstallUI(isIos() ? 'ios' : 'android');
    });

    // 4. Detectar si la app fue instalada con éxito
    window.addEventListener('appinstalled', () => {
        console.log('[PWA] ¡Aplicación instalada con éxito!');
        deferredPrompt = null;
        removeInstallUI();
    });

    // 5. Vincular botones de instalación de cabeceras
    bindInstallButtons();

    // 6. Si no se ha cerrado la pancarta en la sesión y no se ha mostrado aún:
    if (!sessionStorage.getItem('pwa_banner_dismissed')) {
        setTimeout(() => {
            if (isIos()) {
                showInstallUI('ios');
            } else {
                showInstallUI('android');
            }
        }, 2000);
    }
}

function bindInstallButtons() {
    const buttons = [
        document.getElementById('navInstallAppBtn'),
        document.getElementById('pwa-header-install-btn')
    ];

    buttons.forEach(btn => {
        if (btn) {
            if (isInStandaloneMode()) {
                btn.style.display = 'none';
            } else {
                btn.onclick = () => {
                    if (deferredPrompt) {
                        deferredPrompt.prompt();
                    } else if (isIos()) {
                        showIosModal();
                    } else {
                        showAndroidModal();
                    }
                };
            }
        }
    });
}

/**
 * Crea o despliega la interfaz flotante para instalar la PWA
 */
function showInstallUI(platform) {
    if (document.getElementById('pwa-install-banner') || isInStandaloneMode()) return;

    const banner = document.createElement('div');
    banner.id = 'pwa-install-banner';
    banner.className = 'pwa-install-banner';

    if (platform === 'ios') {
        banner.innerHTML = `
            <div class="pwa-banner-content">
                <img src="./pwa-192x192.png" alt="Detectimáticas" class="pwa-banner-icon" />
                <div class="pwa-banner-text">
                    <strong>¿Instalar Detectimáticas?</strong>
                    <span>Instálala en tu iPhone / iPad para acceder sin conexión</span>
                </div>
            </div>
            <div class="pwa-banner-actions">
                <button id="pwa-ios-help-btn" class="pwa-btn-primary">
                    <i data-lucide="download"></i> Instalar App
                </button>
                <button id="pwa-close-btn" class="pwa-btn-close" title="Cerrar">&times;</button>
            </div>
        `;
    } else {
        banner.innerHTML = `
            <div class="pwa-banner-content">
                <img src="./pwa-192x192.png" alt="Detectimáticas" class="pwa-banner-icon" />
                <div class="pwa-banner-text">
                    <strong>¡Instala Detectimáticas!</strong>
                    <span>Acceso rápido con icono propio y modo pantalla completa</span>
                </div>
            </div>
            <div class="pwa-banner-actions">
                <button id="pwa-install-btn" class="pwa-btn-primary">
                    <i data-lucide="download"></i> Instalar App
                </button>
                <button id="pwa-close-btn" class="pwa-btn-close" title="Cerrar">&times;</button>
            </div>
        `;
    }

    document.body.appendChild(banner);

    // Inicializar iconos Lucide
    if (typeof lucide !== 'undefined' && lucide.createIcons) {
        lucide.createIcons();
    }

    // Handlers de botones
    const closeBtn = document.getElementById('pwa-close-btn');
    if (closeBtn) {
        closeBtn.addEventListener('click', () => {
            sessionStorage.setItem('pwa_banner_dismissed', '1');
            banner.classList.add('pwa-banner-hide');
            setTimeout(() => banner.remove(), 400);
        });
    }

    const installBtn = document.getElementById('pwa-install-btn');
    if (installBtn) {
        installBtn.addEventListener('click', async () => {
            if (deferredPrompt) {
                deferredPrompt.prompt();
                const { outcome } = await deferredPrompt.userChoice;
                console.log(`[PWA] Respuesta a la instalación: ${outcome}`);
                deferredPrompt = null;
                banner.remove();
            } else {
                // Si el evento nativo aún no ha saltado, mostrar el tutorial de instalación Android
                showAndroidModal();
            }
        });
    }

    const iosBtn = document.getElementById('pwa-ios-help-btn');
    if (iosBtn) {
        iosBtn.addEventListener('click', () => {
            showIosModal();
        });
    }

    // Auto-cierre discreto tras 7 segundos en smartphones para no estorbar la navegación
    setTimeout(() => {
        const activeBanner = document.getElementById('pwa-install-banner');
        if (activeBanner && !sessionStorage.getItem('pwa_banner_dismissed')) {
            activeBanner.classList.add('pwa-banner-hide');
            setTimeout(() => activeBanner.remove(), 400);
        }
    }, 7000);
}

function removeInstallUI() {
    const banner = document.getElementById('pwa-install-banner');
    if (banner) banner.remove();
}

/**
 * Modal explicativo para instalación manual en Android
 */
export function showAndroidModal() {
    if (document.getElementById('pwa-android-modal')) return;

    const modal = document.createElement('div');
    modal.id = 'pwa-android-modal';
    modal.className = 'pwa-ios-modal-overlay';
    modal.innerHTML = `
        <div class="pwa-ios-modal-card">
            <button class="pwa-modal-close" id="pwa-android-modal-close">&times;</button>
            <div class="pwa-modal-header">
                <img src="./pwa-192x192.png" alt="Detectimáticas Icon" class="pwa-modal-icon" />
                <div>
                    <h3>Instalar Detectimáticas en Android</h3>
                    <p style="margin:0; font-size:0.85rem; color:#64748b;">Disfruta de la app como si fuera nativa</p>
                </div>
            </div>
            <div class="pwa-modal-steps">
                <div class="pwa-step">
                    <span class="step-num">1</span>
                    <p>En tu navegador Chrome, pulsa el botón de <strong>Menú (⋮)</strong> arriba a la derecha.</p>
                </div>
                <div class="pwa-step">
                    <span class="step-num">2</span>
                    <p>Selecciona <strong>"Añadir a la pantalla de inicio"</strong> o <strong>"Instalar aplicación"</strong>.</p>
                </div>
                <div class="pwa-step">
                    <span class="step-num">3</span>
                    <p>Pulsa <strong>Instalar</strong>. Se creará el icono de Detectimáticas en tu móvil.</p>
                </div>
            </div>
            <button class="pwa-btn-understand" id="pwa-android-modal-ok">Entendido</button>
        </div>
    `;

    document.body.appendChild(modal);

    const closeBtn = document.getElementById('pwa-android-modal-close');
    const okBtn = document.getElementById('pwa-android-modal-ok');
    const closeModal = () => modal.remove();

    if (closeBtn) closeBtn.addEventListener('click', closeModal);
    if (okBtn) okBtn.addEventListener('click', closeModal);
    modal.addEventListener('click', (e) => {
        if (e.target === modal) closeModal();
    });
}

/**
 * Modal explicativo para instalación manual en iOS Safari
 */
export function showIosModal() {
    if (document.getElementById('pwa-ios-modal')) return;

    const modal = document.createElement('div');
    modal.id = 'pwa-ios-modal';
    modal.className = 'pwa-ios-modal-overlay';
    modal.innerHTML = `
        <div class="pwa-ios-modal-card">
            <button class="pwa-modal-close" id="pwa-ios-modal-close">&times;</button>
            <div class="pwa-modal-header">
                <img src="./pwa-192x192.png" alt="Detectimáticas Icon" class="pwa-modal-icon" />
                <div>
                    <h3>Instalar Detectimáticas en iPhone/iPad</h3>
                    <p style="margin:0; font-size:0.85rem; color:#64748b;">Acceso directo desde tu pantalla de inicio</p>
                </div>
            </div>
            <div class="pwa-modal-steps">
                <div class="pwa-step">
                    <span class="step-num">1</span>
                    <p>En Safari, pulsa el botón <strong>Compartir</strong> <span class="ios-icon">⎋</span> en la barra inferior.</p>
                </div>
                <div class="pwa-step">
                    <span class="step-num">2</span>
                    <p>Desplázate hacia abajo y elige <strong>Añadir a la pantalla de inicio</strong> <span class="ios-icon">➕</span>.</p>
                </div>
                <div class="pwa-step">
                    <span class="step-num">3</span>
                    <p>Pulsa <strong>Añadir</strong> arriba a la derecha. ¡Y listo!</p>
                </div>
            </div>
            <button class="pwa-btn-understand" id="pwa-ios-modal-ok">Entendido</button>
        </div>
    `;

    document.body.appendChild(modal);

    const closeBtn = document.getElementById('pwa-ios-modal-close');
    const okBtn = document.getElementById('pwa-ios-modal-ok');
    const closeModal = () => modal.remove();

    if (closeBtn) closeBtn.addEventListener('click', closeModal);
    if (okBtn) okBtn.addEventListener('click', closeModal);
    modal.addEventListener('click', (e) => {
        if (e.target === modal) closeModal();
    });
}

// Auto-inicialización
if (typeof window !== 'undefined') {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initPWAInstaller);
    } else {
        initPWAInstaller();
    }
}
